import { useMemo } from 'react'
import { useInfiniteQuery, useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, PageView, PageTopBar } from '../../../../components'
import { Loader } from '../../../../components/Loader'
import { fetchWarehouse } from '../../../../services/warehouseSevices'
import { fetchReceivingHistory } from '../../../../services/inboundAppointmentServices'
import { ReceivingTabs } from '../ReceivingTabs'
import { ReceivingHeader } from '../ReceivingHeader'
import { HistoryEntry } from './HistoryEntry'
import { FIVE_MINUTES_MS } from '../../../../helpers/cacheTimes'

const PAGE = 20

// Defensive: pages are windowed by before_id = previous page's last row id, so
// consecutive pages should never share a row. If a cursor edge case ever repeats
// one anyway, keep only its first occurrence — both to avoid showing the same
// audit entry twice and to keep the list key (row.id) collision-free.
const dedupeById = rows => {
  const seen = new Set()
  return rows.filter(row => {
    if (seen.has(row.id)) return false
    seen.add(row.id)
    return true
  })
}

export const ReceivingHistoryContainer = () => {
  const { warehouse_id } = useParams()
  const { t } = useTranslation()
  const { data: warehouse } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    enabled: !!warehouse_id,
  })

  const query = useInfiniteQuery(
    ['receiving_history', warehouse_id],
    ({ pageParam }) => fetchReceivingHistory(warehouse_id, { limit: PAGE, before_id: pageParam }),
    {
      getNextPageParam: last => (last.has_more && last.rows.length > 0 ? last.rows[last.rows.length - 1].id : undefined),
      enabled: !!warehouse_id,
    },
  )
  // Keyed on the pages themselves so that toggling isFetchingNextPage — which happens on every
  // "Cargar más" — does not re-flatten and re-dedupe every page already loaded.
  const rows = useMemo(() => dedupeById((query.data?.pages || []).flatMap(p => p.rows)), [query.data])

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <ReceivingHeader warehouse={warehouse} />
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <ReceivingTabs />
      <div className="p-4 max-w-4xl">
        {query.isError ? (
          <div className="text-center py-12">
            <p className="text-red-600 font-medium mb-3">{t('receiving_config.history.load_error')}</p>
            <Button type="secondary" onClick={() => query.refetch()}>
              {t('receiving_config.retry')}
            </Button>
          </div>
        ) : query.isLoading ? (
          <Loader show={true} />
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-500 py-12">{t('receiving_config.history.empty')}</p>
        ) : (
          <>
            <ul className="space-y-3">
              {rows.map(row => (
                <HistoryEntry key={row.id} row={row} />
              ))}
            </ul>
            {query.hasNextPage && (
              <div className="flex justify-center mt-4">
                <Button type="secondary" onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
                  {t('receiving_config.history.load_more')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PageView>
  )
}
